/**
 * QR Code Style Modal - Customize QR code appearance
 */
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, QrCode, Download, Palette, Copy, Check } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const QRStyleModal = ({ isOpen, onClose, nfcCode, onDownload }) => {
  const [fgColor, setFgColor] = useState('#000000');
  const [bgColor, setBgColor] = useState('#FFFFFF');
  const [size, setSize] = useState('M');
  const [dotStyle, setDotStyle] = useState('square');
  const [copied, setCopied] = useState(false);

  const handleDownload = () => {
    onDownload(nfcCode, {
      fgColor,
      bgColor,
      size,
      dotStyle
    });
    onClose();
  };

  const handleCopyToClipboard = async () => {
    try {
      // Build the QR URL with current options
      const params = new URLSearchParams();
      if (fgColor) params.append('fg_color', fgColor.replace('#', ''));
      if (bgColor) params.append('bg_color', bgColor.replace('#', ''));
      if (size) params.append('size', size);
      if (dotStyle) params.append('dot_style', dotStyle);
      
      const qrUrl = `${BACKEND_URL}/api/admin/qr-code/${nfcCode}?${params.toString()}`;
      
      // Fetch the QR image
      const response = await fetch(qrUrl);
      const blob = await response.blob();
      
      // Copy to clipboard
      await navigator.clipboard.write([
        new ClipboardItem({ 'image/png': blob })
      ]);
      
      setCopied(true);
      toast.success('QR code copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Copy failed:', error);
      toast.error('Failed to copy. Try downloading instead.');
    }
  };

  const previewUrl = `${BACKEND_URL}/api/admin/qr-code/${nfcCode}?fg_color=${fgColor.replace('#', '')}&bg_color=${bgColor.replace('#', '')}&size=${size}&dot_style=${dotStyle}`;

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 overflow-y-auto">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="bg-background rounded-3xl shadow-2xl w-full max-w-lg my-4 max-h-[90vh] flex flex-col"
        >
          {/* Header */}
          <div className="p-4 sm:p-6 border-b border-border flex items-center justify-between shrink-0">
            <div className="flex items-center gap-3">
              <QrCode className="w-6 h-6 text-primary" />
              <div>
                <h2 className="text-lg sm:text-xl font-semibold">Customize QR Code</h2>
                <p className="text-xs sm:text-sm text-muted-foreground">Style your QR code</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-secondary rounded-full transition-colors"
              data-testid="qr-modal-close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="p-4 sm:p-6 space-y-4 sm:space-y-6 overflow-y-auto flex-1">
            {/* Preview */}
            <div className="flex justify-center">
              <div className="p-3 sm:p-4 rounded-2xl bg-secondary/30 border border-border">
                <img
                  src={previewUrl}
                  alt="QR Code Preview"
                  className="w-32 h-32 sm:w-48 sm:h-48 object-contain"
                  data-testid="qr-preview"
                />
              </div>
            </div>

            {/* Color Options */}
            <div className="grid grid-cols-2 gap-3 sm:gap-4">
              <div>
                <label className="block text-sm font-medium mb-2 flex items-center gap-2">
                  <Palette className="w-4 h-4 text-muted-foreground" />
                  Foreground
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={fgColor}
                    onChange={(e) => setFgColor(e.target.value)}
                    className="w-10 h-8 sm:w-12 sm:h-10 rounded cursor-pointer border border-border"
                    data-testid="qr-fg-color"
                  />
                  <input
                    type="text"
                    value={fgColor}
                    onChange={(e) => setFgColor(e.target.value)}
                    className="flex-1 px-2 sm:px-3 py-2 bg-secondary rounded-lg text-xs sm:text-sm font-mono min-w-0"
                    placeholder="#000000"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2 flex items-center gap-2">
                  <Palette className="w-4 h-4 text-muted-foreground" />
                  Background
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={bgColor}
                    onChange={(e) => setBgColor(e.target.value)}
                    className="w-10 h-8 sm:w-12 sm:h-10 rounded cursor-pointer border border-border"
                    data-testid="qr-bg-color"
                  />
                  <input
                    type="text"
                    value={bgColor}
                    onChange={(e) => setBgColor(e.target.value)}
                    className="flex-1 px-2 sm:px-3 py-2 bg-secondary rounded-lg text-xs sm:text-sm font-mono min-w-0"
                    placeholder="#FFFFFF"
                  />
                </div>
              </div>
            </div>

            {/* Size Options */}
            <div>
              <label className="block text-sm font-medium mb-2">Size</label>
              <div className="grid grid-cols-4 gap-2">
                {[
                  { value: 'S', label: 'S' },
                  { value: 'M', label: 'M' },
                  { value: 'L', label: 'L' },
                  { value: 'XL', label: 'XL' }
                ].map((option) => (
                  <button
                    key={option.value}
                    onClick={() => setSize(option.value)}
                    className={`px-2 py-2 rounded-lg text-sm font-medium transition-all ${
                      size === option.value
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-secondary hover:bg-secondary/80'
                    }`}
                    data-testid={`qr-size-${option.value}`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Dot Style Options */}
            <div>
              <label className="block text-sm font-medium mb-2">Dot Style</label>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { value: 'square', label: 'Square', icon: '■' },
                  { value: 'rounded', label: 'Rounded', icon: '▢' },
                  { value: 'dots', label: 'Dots', icon: '●' },
                  { value: 'vertical_bars', label: 'V-Bars', icon: '║' },
                  { value: 'horizontal_bars', label: 'H-Bars', icon: '═' },
                  { value: 'gapped', label: 'Gapped', icon: '▫' }
                ].map((style) => (
                  <button
                    key={style.value}
                    onClick={() => setDotStyle(style.value)}
                    className={`px-2 py-2 rounded-lg text-sm font-medium transition-all flex flex-col items-center gap-1 ${
                      dotStyle === style.value
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-secondary hover:bg-secondary/80'
                    }`}
                    data-testid={`qr-dot-${style.value}`}
                  >
                    <span className="text-lg">{style.icon}</span>
                    <span className="text-xs">{style.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Quick Color Presets */}
            <div>
              <label className="block text-sm font-medium mb-2">Quick Presets</label>
              <div className="flex flex-wrap gap-2">
                {[
                  { fg: '#000000', bg: '#FFFFFF', name: 'Classic' },
                  { fg: '#1D4ED8', bg: '#DBEAFE', name: 'Blue' },
                  { fg: '#059669', bg: '#D1FAE5', name: 'Green' },
                  { fg: '#7C3AED', bg: '#EDE9FE', name: 'Purple' },
                  { fg: '#DC2626', bg: '#FEE2E2', name: 'Red' },
                  { fg: '#FFFFFF', bg: '#000000', name: 'Inverted' },
                ].map((preset) => (
                  <button
                    key={preset.name}
                    onClick={() => {
                      setFgColor(preset.fg);
                      setBgColor(preset.bg);
                    }}
                    className="px-2 sm:px-3 py-1.5 rounded-lg text-xs font-medium bg-secondary hover:bg-secondary/80 transition-all flex items-center gap-1.5"
                    data-testid={`qr-preset-${preset.name.toLowerCase()}`}
                  >
                    <div
                      className="w-4 h-4 rounded border"
                      style={{ background: preset.bg, borderColor: preset.fg }}
                    >
                      <div
                        className="w-2 h-2 m-0.5 rounded-sm"
                        style={{ background: preset.fg }}
                      />
                    </div>
                    {preset.name}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="p-4 sm:p-6 border-t border-border flex justify-end gap-3 shrink-0">
            <button
              onClick={onClose}
              className="px-4 sm:px-6 py-2.5 rounded-full bg-secondary font-medium text-sm"
              data-testid="qr-modal-cancel"
            >
              Cancel
            </button>
            <button
              onClick={handleCopyToClipboard}
              className="px-4 sm:px-6 py-2.5 rounded-full bg-secondary font-medium flex items-center gap-2 text-sm hover:bg-secondary/80 transition-colors"
              data-testid="qr-modal-copy"
            >
              {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
              {copied ? 'Copied!' : 'Copy'}
            </button>
            <button
              onClick={handleDownload}
              className="px-4 sm:px-6 py-2.5 rounded-full bg-primary text-primary-foreground font-medium flex items-center gap-2 text-sm"
              data-testid="qr-modal-download"
            >
              <Download className="w-4 h-4" />
              Download
            </button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default QRStyleModal;
